import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { OrderItemsPreMediaControllerApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(OrderItemsPreMediaControllerApi);

export const useOrderItemsPreMedia = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useRequestPreMediaRequestDetails = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemsPreMediaControllerPatchPreMediaBrief
        >[0]["orderItemPreMediaBriefValidationCommand"];
      }) =>
        apiClient.orderItemsPreMediaControllerPatchPreMediaBrief({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemPreMediaBriefValidationCommand: params.command,
        }),

      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useAddPreMediaClientRequestDetails = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemsPreMediaControllerAddPreMediaBriefDetails
        >[0]["orderPreMediaAddBriefDetailsCommand"];
      }) =>
        apiClient.orderItemsPreMediaControllerAddPreMediaBriefDetails({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderPreMediaAddBriefDetailsCommand: params.command,
        }),

      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useValidateSandboxItemPreMediaRequest = () =>
    useMutation({
      mutationFn: (params: { orderId: string; orderItemId: string }) =>
        apiClient.orderItemsPreMediaControllerPatchPreMediaBrief({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemPreMediaBriefValidationCommand: {
            isValidated: true,
          },
        }),

      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useAddSandboxItemPreMediaPropositionFile = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemsPreMediaControllerAddPreMediaProposition
        >[0]["orderItemAddPreMediaPropositionCommand"];
      }) =>
        apiClient.orderItemsPreMediaControllerAddPreMediaProposition({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemAddPreMediaPropositionCommand: params.command,
        }),

      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  return {
    useRequestPreMediaRequestDetails,
    useAddPreMediaClientRequestDetails,
    useAddSandboxItemPreMediaPropositionFile,
    useAcceptSandboxItemPreMediaRequest: useValidateSandboxItemPreMediaRequest,
  };
};
